import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, PlayCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

// Tipagens
interface PortfolioPhotoCategory {
  id: string;
  name: string;
  images: string[];
}

interface PortfolioVideo {
  id: string;
  title: string;
  thumbnail: string;
  url: string;
  orientation: "16:9" | "9:16";
}

interface PortfolioCourse {
  id: string;
  title: string;
  image: string;
  link: string;
}

const PortfolioImersivo = () => {
  const [photoCategories, setPhotoCategories] = useState<PortfolioPhotoCategory[]>([]);
  const [videos, setVideos] = useState<PortfolioVideo[]>([]);
  const [courses, setCourses] = useState<PortfolioCourse[]>([]);
  
  // States de Expansão
  const [showGallery, setShowGallery] = useState(false);
  const [showVideos, setShowVideos] = useState(false);

  // Landing Page CMS Config
  const [config, setConfig] = useState({
    heroTitle: "Fotografia",
    heroSubtitle: "Vídeos",
    heroDescription: "Transmissão ao vivo",
    heroText: "Produções que conectam marcas, pessoas e histórias de forma autêntica e inesquecível.",
    heroImageUrl: "",
    
    gridTitle1: "A sua",
    gridTitle2: "imagem",
    gridTitle3: "precisa ir além.",
    gridText: "Mais que registros, criamos experiências visuais que comunicam, emocionam e geram impacto.",
    gridButtonText: "Explorar Galeria",
    gridImage1: "",
    gridImage2: "",
    gridImage3: "",

    fontFamily: "Inter"
  });

  useEffect(() => {
    fetchPortfolioData();

    const channel = supabase
      .channel('public:site_settings')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'site_settings' },
        () => {
          fetchPortfolioData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchPortfolioData = async () => {
    try {
      const { data } = await supabase.from("site_settings" as any).select("*") as any;
      if (data && Array.isArray(data)) {
        const pPhotos = data.find((s: any) => s.key === "portfolio_photos")?.value;
        const pVideos = data.find((s: any) => s.key === "portfolio_videos")?.value;
        const pCourses = data.find((s: any) => s.key === "portfolio_courses")?.value;

        if (pPhotos) setPhotoCategories(JSON.parse(pPhotos));
        if (pVideos) setVideos(JSON.parse(pVideos));
        
        let loadedCourses = pCourses ? JSON.parse(pCourses) : [];
        // Hardcode Zero Abalo course so it always appears regardless of DB
        if (!loadedCourses.some((c: any) => c.title === "Zero Abalo")) {
          loadedCourses.push({
            id: "zero-abalo",
            title: "Zero Abalo",
            image: "/capa-zero-abalo.jpg",
            link: "/ebook-arbitro"
          });
        }
        setCourses(loadedCourses);

        setConfig({
          heroTitle: data.find((s: any) => s.key === "hero_title")?.value || "Fotografia",
          heroSubtitle: data.find((s: any) => s.key === "hero_subtitle")?.value || "Vídeos",
          heroDescription: data.find((s: any) => s.key === "hero_description")?.value || "Transmissão ao vivo",
          heroText: data.find((s: any) => s.key === "hero_text")?.value || "Produções que conectam marcas, pessoas e histórias de forma autêntica e inesquecível.",
          heroImageUrl: data.find((s: any) => s.key === "hero_image_url")?.value || "",
          
          gridTitle1: data.find((s: any) => s.key === "grid_title_1")?.value || "A sua",
          gridTitle2: data.find((s: any) => s.key === "grid_title_2")?.value || "imagem",
          gridTitle3: data.find((s: any) => s.key === "grid_title_3")?.value || "precisa ir além.",
          gridText: data.find((s: any) => s.key === "grid_text")?.value || "Mais que registros, criamos experiências visuais que comunicam, emocionam e geram impacto.",
          gridButtonText: data.find((s: any) => s.key === "grid_button_text")?.value || "Explorar Galeria",
          gridImage1: data.find((s: any) => s.key === "grid_image_1")?.value || "",
          gridImage2: data.find((s: any) => s.key === "grid_image_2")?.value || "",
          gridImage3: data.find((s: any) => s.key === "grid_image_3")?.value || "",

          fontFamily: data.find((s: any) => s.key === "font_family")?.value || "Inter"
        });
      }
    } catch (e) {
      console.error("Erro ao buscar dados do portfólio:", e);
    }
  };

  const scrollToElement = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Fallbacks
  const fallbackHeroImage = config.heroImageUrl || photoCategories[0]?.images[0] || "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?q=80&w=2070";
  const fallbackGrid1 = config.gridImage1 || photoCategories[0]?.images[1] || "https://images.unsplash.com/photo-1554048665-27a3c3c7db17?q=80&w=1000";
  const fallbackGrid2 = config.gridImage2 || photoCategories[0]?.images[2] || "https://images.unsplash.com/photo-1533158326339-7f3cf2404354?q=80&w=1000";
  const fallbackGrid3 = config.gridImage3 || photoCategories[0]?.images[3] || "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=1000";

  return (
    <div className="w-full bg-[#050505] text-white relative overflow-hidden font-display" style={{ fontFamily: config.fontFamily }}>
      
      {/* ─── TRILHA SVG ONDULADA (BACKGROUND ESTATICO NEON) ─── */}
      <div 
        className="absolute inset-0 pointer-events-none z-0 flex justify-center opacity-80 md:opacity-100 overflow-hidden max-h-full"
        style={{ contain: 'paint' }}
      >
        <svg 
          viewBox="0 0 1440 3000" 
          className="w-[1440px] md:w-full h-full min-w-[1440px] pointer-events-none" 
          preserveAspectRatio="xMidYMin slice"
        >
          <defs>
            <filter id="neon-glow" x="-50%" y="-50%" width="200%" height="200%">
              {/* Desfoque pesado para criar a aura neon */}
              <feGaussianBlur in="SourceGraphic" stdDeviation="20" result="blur1" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="40" result="blur2" />
              {/* Mescla os desfoques com o traço original */}
              <feMerge>
                <feMergeNode in="blur2" />
                <feMergeNode in="blur1" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Path desenhado com efeito neon aplicado */}
          <path 
            id="curve" 
            d="M 600 -100 C 1200 300, 1500 800, 700 1300 C -100 1800, 100 2400, 1000 3200" 
            fill="transparent" 
            stroke="#FF6A00" 
            strokeWidth="60" 
            strokeLinecap="round" 
            filter="url(#neon-glow)"
          />
          
          {/* Texto fixo sem animação, para eliminar 100% de travamentos */}
          <text width="1440" className="text-[26px] font-black uppercase tracking-[0.4em] fill-black pointer-events-none">
            <textPath href="#curve" startOffset="0%">
              AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • 
            </textPath>
          </text>
        </svg>
      </div>

      {/* ─── NAVEGAÇÃO SUPERIOR ─── */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 py-6 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-transparent pointer-events-auto">
        {/* Logo Oficial HRo Signature na Landing Page */}
        <div className="flex items-center justify-center relative z-10 shrink-0 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img 
            src="/HUDY-MOTION.png" 
            alt="HRo Signature Logo" 
            className="h-14 md:h-16 w-auto object-contain drop-shadow-[0_0_20px_rgba(255,106,0,0.5)] transition-transform hover:scale-105" 
          />
        </div>
        
        <div className="hidden md:flex gap-2 relative z-10 flex-wrap justify-center items-end h-12">
          {["Fotografias", "Vídeos", "Cursos"].map(item => (
            <button 
              key={item}
              onClick={() => scrollToElement(`section-${item.toLowerCase()}`)}
              className="px-6 py-2.5 rounded-[40px] bg-orange-500 text-black text-[10px] font-black uppercase tracking-widest hover:bg-orange-400 hover:scale-125 hover:-translate-y-2 transition-all duration-300 shadow-lg mx-1 origin-bottom"
            >
              {item}
            </button>
          ))}
        </div>
        
        <button className="text-white hover:text-orange-500 transition-colors relative z-10">
          <Menu className="w-8 h-8" />
        </button>
      </nav>

      {/* ─── CONTEÚDO PRINCIPAL ─── */}
      <div className="relative z-10 w-full md:max-w-7xl mx-auto px-4 md:px-12 space-y-32">

        {/* ─── SEÇÃO 1 (HERO) ─── */}
        <section className="pt-12 md:pt-24 flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 space-y-6 lg:pl-32 xl:pl-48">
            <h1 className="text-5xl md:text-7xl font-black text-white leading-[0.9] tracking-tighter">
              {config.heroTitle}<br />
              <span className="text-orange-500">{config.heroSubtitle}</span><br />
              {config.heroDescription}
            </h1>
            <p className="text-sm md:text-base text-gray-300 font-medium max-w-md whitespace-pre-wrap">
              {config.heroText}
            </p>
          </div>
          
          <div className="flex-[1.5] w-full relative">
            {/* Container estendido para a direita (w-[150%]) para vazar a tela e cortar reto, 
                com bordas arredondadas apenas na esquerda (rounded-l) */}
            <div className="w-[120%] lg:w-[150%] aspect-[16/10] lg:aspect-[16/9] rounded-l-[40px] md:rounded-l-[80px] overflow-hidden border-l border-y border-white/10 shadow-[-30px_30px_60px_-15px_rgba(0,0,0,0.8)] relative group bg-[#111]">
              <img src={fallbackHeroImage} alt="Destaque" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            </div>
          </div>
        </section>


        {/* ─── SEÇÃO 2 (GRID & TEXTO) ─── */}
        <section className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:pl-16">
          
          {/* Grid de 3 imagens */}
          <div className="flex-1 w-full flex gap-4 md:gap-6">
            <div className="flex-1 rounded-[30px] md:rounded-[40px] overflow-hidden aspect-[1/2] border border-white/10 shadow-2xl relative group bg-[#111]">
              <img src={fallbackGrid1} alt="Grid 1" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </div>
            <div className="flex-1 flex flex-col gap-4 md:gap-6">
              <div className="flex-1 rounded-[30px] md:rounded-[40px] overflow-hidden border border-white/10 shadow-2xl relative group bg-[#111]">
                <img src={fallbackGrid2} alt="Grid 2" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
              <div className="flex-1 rounded-[30px] md:rounded-[40px] overflow-hidden border border-white/10 shadow-2xl relative group bg-[#111]">
                <img src={fallbackGrid3} alt="Grid 3" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-6">
            <h2 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight">
              {config.gridTitle1}<br />
              <span className="text-orange-500">{config.gridTitle2}</span><br />
              {config.gridTitle3}
            </h2>
            <p className="text-sm md:text-base text-gray-300 font-medium max-w-sm whitespace-pre-wrap">
              {config.gridText}
            </p>
            <div className="pt-4">
              <button 
                onClick={() => {
                  setShowGallery(!showGallery);
                  setTimeout(() => scrollToElement("section-fotografias"), 100);
                }}
                className="group flex items-center gap-4 bg-transparent border border-orange-500/30 hover:border-orange-500 rounded-full px-2 py-2 pr-6 transition-all"
              >
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-black group-hover:scale-110 transition-transform">
                  <ChevronDown className={cn("w-4 h-4 transition-transform duration-500", showGallery && "rotate-180")} />
                </div>
                <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest group-hover:text-orange-400">
                  {config.gridButtonText}
                </span>
              </button>
            </div>
          </div>
        </section>


        {/* ─── GALERIA OCULTA (FOTOS) ─── */}
        <AnimatePresence>
          {showGallery && (
            <motion.section 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
              id="section-fotografias"
            >
              <div className="space-y-24 py-12 border-t border-white/10 mt-12 lg:pl-32 xl:pl-48">
                {photoCategories.map((cat, idx) => (
                  <div key={cat.id} className="space-y-8">
                    <div className="flex items-center gap-4">
                      <h2 className="text-3xl md:text-5xl font-black  uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                        {cat.name}
                      </h2>
                      <div className="h-0.5 flex-1 bg-gradient-to-r from-orange-500/50 to-transparent" />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {cat.images.map((img, i) => (
                        <motion.div 
                          key={i}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.1 }}
                          className="aspect-[4/5] rounded-[32px] overflow-hidden bg-[#111] relative group cursor-pointer border border-white/10"
                        >
                          <img src={img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={`${cat.name} ${i}`} />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500 flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 text-white font-black uppercase tracking-widest text-[10px] translate-y-4 group-hover:translate-y-0 transition-all">Ver em tela cheia</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* ─── SEÇÃO VÍDEOS EXPANSÍVEIS ─── */}
        <section id="section-vídeos" className="lg:pl-32 xl:pl-48 flex flex-col items-start md:items-center">
          <button 
            onClick={() => setShowVideos(!showVideos)}
            className="group flex flex-col items-start md:items-center gap-4 py-8"
          >
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-500">Assistir Produções</span>
            <div className="w-12 h-12 rounded-full border border-orange-500 bg-black/50 text-orange-500 flex items-center justify-center shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all group-hover:scale-110 group-hover:bg-orange-500 group-hover:text-black">
              <ChevronDown className={cn("w-6 h-6 transition-transform duration-500", showVideos && "rotate-180")} />
            </div>
          </button>

          <AnimatePresence>
            {showVideos && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden w-full"
              >
                <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {videos.map((vid, i) => (
                    <motion.div 
                      key={vid.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      onClick={() => window.open(vid.url, "_blank")}
                      className={`relative rounded-[32px] overflow-hidden bg-[#111] group cursor-pointer border border-white/10 ${vid.orientation === '16:9' ? 'aspect-video md:col-span-2' : 'aspect-[9/16]'}`}
                    >
                      <img src={vid.thumbnail} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={vid.title} />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-orange-500/90 backdrop-blur text-black flex items-center justify-center scale-90 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 shadow-[0_0_30px_rgba(255,106,0,0.6)]">
                          <PlayCircle className="w-8 h-8" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <p className="text-white font-black uppercase tracking-widest text-[10px]">{vid.title}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* ─── SEÇÃO CURSOS ─── */}
        <section id="section-cursos" className="pt-12 pb-24 px-6 md:px-16 max-w-7xl mx-auto relative z-20">
          <div className="text-center mb-12 relative">
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none mb-4 text-white">
              Cursos & <span className="text-orange-500">Mentorias</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {courses.map((course, i) => (
              <motion.a
                href={course.link}
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative w-full aspect-[2/3] rounded-xl md:rounded-2xl overflow-hidden bg-[#0d0d0d] border border-white/10 hover:border-orange-500/60 shadow-2xl transition-all duration-500 flex flex-col justify-end p-6"
              >
                {/* Imagem do Poster de Capa */}
                <img 
                  src={course.image} 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                  alt={course.title} 
                />

                {/* Sombra de Degradê Estilo Netflix */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-500" />
                
                {/* Conteúdo & Botão no Estilo Cartaz */}
                <div className="relative z-10 space-y-3">
                  <span className="inline-block px-3 py-1 bg-orange-500/20 border border-orange-500/40 text-orange-500 text-[9px] font-black uppercase tracking-widest rounded-md">
                    Ebook & Curso
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight leading-tight group-hover:text-orange-400 transition-colors">
                    {course.title}
                  </h3>
                  <div className="w-full py-3 bg-orange-500 text-black text-xs font-black uppercase rounded-lg tracking-widest text-center shadow-lg group-hover:bg-orange-400 transition-all duration-300 transform group-hover:translate-y-0 opacity-90 group-hover:opacity-100">
                    Acessar Curso
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </section>

        {/* ─── FOOTER INTEGRADO ─── */}
        <footer className="w-full pt-16 pb-12 border-t border-white/10 bg-[#050505] relative z-20 text-center space-y-4">
          <div className="flex items-center justify-center gap-6 text-xs uppercase tracking-widest font-black text-zinc-400">
            <a href="/privacy" className="hover:text-orange-500 transition-colors">Políticas de Privacidade</a>
            <span>•</span>
            <a href="/subscription" className="hover:text-orange-500 transition-colors">Termos de Uso</a>
          </div>
          <p className="text-[11px] text-zinc-500 font-bold uppercase tracking-wider">
            Seus dados de pagamento são protegidos e salvos via Mercado Pago 🛡️
          </p>
          <div className="pt-4 text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600">
            © 2026 BY HUDSON RAONE • TODOS OS DIREITOS RESERVADOS
          </div>
        </footer>

      </div>
    </div>
  );
};

export default PortfolioImersivo;
